import { useEffect, useMemo, useState, type ReactNode } from "react";
import { authLogout, setAuthChangedHandler, tryRefresh } from "../api/client";
import { AuthCtx, type AuthState } from "./context";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthState["user"]>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  useEffect(() => {
    setAuthChangedHandler((u) => setUser(u));
    let cancelled = false;
    tryRefresh()
      .then((ok) => {
        if (cancelled) return;
        setStatus(ok ? "authenticated" : "unauthenticated");
      })
      .catch(() => !cancelled && setStatus("unauthenticated"));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status === "loading") return;
    setStatus(user ? "authenticated" : "unauthenticated");
  }, [user, status]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      setUser,
      logout: async () => {
        await authLogout();
        setUser(null);
      },
    }),
    [user, status],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
